import React, { useState, useEffect, useRef } from 'react';
import { Form, InputGroup, Button, Spinner, Badge, ListGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilter, faTimes, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import SearchFilters from './SearchFilters';
import SearchResults from './SearchResults';
import { searchMessages } from '../../services/messagingService';
import './MessageSearchPanel.css';

/**
 * MessageSearchPanel Component
 * 
 * Provides comprehensive message search with filtering options.
 * 
 * Features:
 * - Full-text search across messages
 * - Advanced filtering by sender, date, and type
 * - Search suggestions and auto-complete
 * - Result highlighting
 * - Responsive design
 * 
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5
 */
function MessageSearchPanel({ onSelectThread, onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    sender: '',
    dateFrom: '',
    dateTo: '',
    messageType: '',
    threadId: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceTimer = useRef(null);
  const searchInputRef = useRef(null);

  // Load recent searches from localStorage for suggestions
  useEffect(() => {
    const recentSearches = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    setSuggestions(recentSearches.slice(0, 5));
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    debounceTimer.current = setTimeout(() => {
      performSearch(searchQuery, appliedFilters);
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery, appliedFilters]);

  const performSearch = async (query, currentFilters = {}) => {
    try {
      setLoading(true);
      
      // Build search parameters
      const searchParams = {
        query: query.trim(),
        ...currentFilters
      };

      const response = await searchMessages(query, searchParams);
      
      // Handle response format
      const results = response.results || [];
      setSearchResults(results);

      // Save to recent searches
      saveRecentSearch(query);
    } catch (error) {
      console.error('Error searching messages:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const saveRecentSearch = (query) => {
    if (!query.trim()) return;
    
    const recentSearches = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 10);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
    setSuggestions(updated.slice(0, 5));
  };

  const handleApplyFilters = (newFilters) => {
    setAppliedFilters(newFilters);
    setShowFilters(false);
    
    if (searchQuery.trim()) {
      performSearch(searchQuery, newFilters);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      sender: '',
      dateFrom: '',
      dateTo: '',
      messageType: '',
      threadId: ''
    });
    setAppliedFilters({});
    
    if (searchQuery.trim()) {
      performSearch(searchQuery, {});
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    performSearch(suggestion, appliedFilters);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSuggestions(false);
  };

  const activeFilterCount = Object.values(appliedFilters).filter(v => v).length;

  return (
    <div className="message-search-panel">
      <div className="search-panel-header">
        <h5 className="mb-3">
          <FontAwesomeIcon icon={faSearch} className="me-2" />
          Search Messages
        </h5>

        {/* Search Input */}
        <div className="search-input-container">
          <InputGroup className="mb-3">
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <Form.Control
              ref={searchInputRef}
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setShowSuggestions(suggestions.length > 0 && !searchQuery)}
              className="search-input"
            />
            {searchQuery && (
              <Button
                variant="outline-secondary"
                onClick={handleClearSearch}
                className="clear-search-btn"
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            )}
            {loading && (
              <InputGroup.Text>
                <Spinner animation="border" size="sm" />
              </InputGroup.Text>
            )}
          </InputGroup>

          {/* Search Suggestions */}
          {showSuggestions && suggestions.length > 0 && !searchQuery && (
            <div className="search-suggestions">
              <div className="suggestions-header">Recent Searches</div>
              <ListGroup>
                {suggestions.map((suggestion, index) => (
                  <ListGroup.Item
                    key={index}
                    action
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="suggestion-item"
                  >
                    <FontAwesomeIcon icon={faSearch} className="me-2 text-muted" />
                    {suggestion}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}
        </div>

        {/* Filter Controls */}
        <div className="filter-controls d-flex justify-content-between align-items-center mb-3">
          <Button
            variant={showFilters ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="filter-toggle-btn"
          >
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            Filters
            {activeFilterCount > 0 && (
              <Badge bg="light" text="primary" className="ms-2">
                {activeFilterCount}
              </Badge>
            )}
            <FontAwesomeIcon
              icon={showFilters ? faChevronUp : faChevronDown}
              className="ms-2"
              size="sm"
            />
          </Button>

          {activeFilterCount > 0 && (
            <Button
              variant="link"
              size="sm"
              onClick={handleClearFilters}
              className="clear-filters-btn"
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <SearchFilters
            filters={filters}
            onFiltersChange={setFilters}
            onApply={handleApplyFilters}
            onCancel={() => setShowFilters(false)}
          />
        )}
      </div>

      {/* Search Results */}
      <div className="search-panel-body">
        <SearchResults
          results={searchResults}
          searchQuery={searchQuery}
          loading={loading}
          onSelectThread={onSelectThread}
          filters={appliedFilters}
        />
      </div>

      {/* Close Button */}
      {onClose && (
        <div className="search-panel-footer">
          <Button variant="outline-secondary" onClick={onClose} className="w-100">
            Close Search
          </Button>
        </div>
      )}
    </div>
  );
}

export default MessageSearchPanel;
